'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

/** Decorative footage; product UI remains a separate, crisp DOM layer. */
export function WebsiteLandscapeVideo({
  src,
  className,
  poster = '/images/editorial/product-horizon.png',
}: {
  src: string;
  className?: string;
  poster?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    const sync = () => {
      if (!visible || document.hidden || motion.matches) {
        video.pause();
        if (motion.matches) setPlaying(false);
        return;
      }
      if (video.getAttribute('src') !== src) video.src = src;
      if (video.error) video.load();
      void video.play().catch(() => setPlaying(false));
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    observer.observe(video);
    motion.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('online', sync);
    return () => {
      video.pause();
      observer.disconnect();
      motion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('online', sync);
    };
  }, [src]);
  return (
    <>
      <Image src={poster} alt="" fill sizes="100vw" className={className} />
      <video
        ref={ref}
        className={className}
        style={{ opacity: playing ? 1 : 0 }}
        onPlaying={() => setPlaying(true)}
        onWaiting={() => setPlaying(false)}
        onError={() => setPlaying(false)}
        onEmptied={() => setPlaying(false)}
        data-landscape-video
        muted
        loop
        playsInline
        preload="none"
        poster={poster}
        aria-hidden="true"
        tabIndex={-1}
      />
    </>
  );
}
